import { Timestamp } from "firebase/firestore";

export function formatTimeString(
  dateString: string | Date | Timestamp,
  excludeDate?: boolean,
  userTimeZone?: string, // Optional parameter for user-specified time zone
): string {
  // Ensure dateString is a Date object
  let utcDate = reconcileDateString(dateString);

  const options: Intl.DateTimeFormatOptions = {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
    timeZone: userTimeZone || Intl.DateTimeFormat().resolvedOptions().timeZone, // Use provided time zone or user's current time zone
  };

  if (!excludeDate) {
    options.day = "2-digit";
    options.month = "short";
  }

  const finalTimeString = new Intl.DateTimeFormat("en-US", options).format(
    utcDate,
  );
  return finalTimeString;
}

export function formatProfileDate(dateString: string): string {
  const joinDate = new Date(dateString);
  const now = new Date();

  // Calculate initial differences
  let years = now.getFullYear() - joinDate.getFullYear();
  let months = now.getMonth() - joinDate.getMonth();
  let days = now.getDate() - joinDate.getDate();

  // Adjust for negative months and days
  if (days < 0) {
    months--;
    days += new Date(now.getFullYear(), now.getMonth(), 0).getDate();
  }
  if (months < 0) {
    years--;
    months += 12;
  }

  // Calculate weeks and remaining days
  const weeks = Math.floor(days / 7);
  days %= 7;

  // Collect the first two non-zero intervals
  const resultParts: string[] = [];
  if (years > 0) resultParts.push(`${years}y`);
  if (months > 0 && resultParts.length < 2) resultParts.push(`${months}m`);
  if (weeks > 0 && resultParts.length < 2) resultParts.push(`${weeks}w`);
  if (days > 0 && resultParts.length < 2) resultParts.push(`${days}d`);

  return `Joined ${resultParts.join(" ")}`;
}

export function isNewDate(dateString: string): boolean {
  // Parse the input date string into a Date object
  const inputDate = new Date(dateString);

  // Check if the date is valid
  if (isNaN(inputDate.getTime())) {
    throw new Error("Invalid date format. Please use a valid date string.");
  }

  // Get the current date and time
  const now = new Date();

  // Calculate the absolute difference in milliseconds
  const delta = Math.abs(now.getTime() - inputDate.getTime());

  // Calculate the difference in days
  const deltaDays = delta / (1000 * 60 * 60 * 24);

  // Check if the difference is less than 2 days
  return deltaDays < 2;
}

export function reconcileDateString(date: string | Date | Timestamp): Date {
  let inputDate;
  if (date instanceof Timestamp) inputDate = date.toDate();
  else if (date instanceof Date) inputDate = date;
  else inputDate = new Date(date);
  return inputDate;
}

export function formatDate(dateString: string | Date | Timestamp): string {
  const inputDate = reconcileDateString(dateString);
  if (isNaN(inputDate.getTime())) return "-";

  // Fixed month names: en-GB's "short" month is "Sept" for September but three
  // letters for every other month.
  return `${String(inputDate.getDate()).padStart(2, "0")} ${SHORT_MONTHS[inputDate.getMonth()]} ${inputDate.getFullYear()}`;
}

export const SHORT_MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
